import { type Auth } from "api/models";
import { makeAutoObservable } from "mobx";
import { assert } from "utils";

import { getInitials } from "src/helpers/getInitials";

type SuccessAuthenticate = Extract<
  Auth.Authenticate,
  { status: "success" }
>["data"];

export type MaybeAuthAuthenticate = SuccessAuthenticate | null;

export class AuthStore {
  _authenticatedData: MaybeAuthAuthenticate;

  constructor(a: MaybeAuthAuthenticate) {
    this._authenticatedData = a;
    makeAutoObservable(this);
  }

  load(authenticatedData: SuccessAuthenticate) {
    this._authenticatedData = authenticatedData;
  }

  get isAuthenticated(): boolean {
    return !!this._authenticatedData;
  }

  get authenticatedData() {
    assert(this._authenticatedData);
    return this._authenticatedData;
  }

  get user() {
    assert(this.authenticatedData.user);
    return this.authenticatedData.user;
  }

  get isCoach() {
    try {
      return this.authenticatedData.coach !== null;
    } catch (e) {
      return false;
    }
  }

  get isAthlete() {
    return this.authenticatedData.athlete !== null;
  }

  get coach() {
    assert(this.authenticatedData.coach);
    return this.authenticatedData.coach;
  }

  get coachSafe() {
    try {
      return this.coach;
    } catch (e) {
      return null;
    }
  }

  get athleteSafe() {
    try {
      return this.athlete;
    } catch (e) {
      return null;
    }
  }

  get athlete() {
    assert(this.authenticatedData.athlete);
    return this.authenticatedData.athlete;
  }

  get isAdmin() {
    return this.authenticatedData.user.role === "ADMIN";
  }

  get initials() {
    const name = this.authenticatedData.user.name;
    return getInitials(name);
  }

  get isFreeTier() {
    try {
      return this.user.tier === "FREE";
    } catch (e) {
      return true;
    }
  }
}
